import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Utensils, Stethoscope, TrendingUp, Sparkles, CreditCard, UserCheck, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Template {
  id: string;
  name: string;
  icon: typeof Utensils;
  description: string;
  features: string[];
  config: {
    callType: string;
    purpose: string;
    tone: string;
    features: string[];
    systemPrompt: string;
  };
}

interface TemplateSelectorProps {
  onSelect: (config: any) => void;
  onBack: () => void;
  onCustom: () => void;
}

const templates: Template[] = [
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: Utensils,
    description: 'Perfect for taking reservations, managing waitlists, and answering customer questions',
    features: [
      'Take reservations 24/7',
      'Manage waitlist',
      'Answer menu questions',
      'Handle special requests'
    ],
    config: {
      callType: 'incoming',
      purpose: 'reservations',
      tone: 'friendly',
      features: ['appointments', 'information', 'transfer'],
      systemPrompt: 'You are a friendly restaurant host helping with reservations, waitlist management, and answering questions about the menu and special events.'
    }
  },
  {
    id: 'medical',
    name: 'Medical Office',
    icon: Stethoscope,
    description: 'Ideal for appointment scheduling, reminders, and patient information collection',
    features: [
      'Schedule appointments',
      'Send reminders',
      'Collect insurance info',
      'HIPAA-compliant responses'
    ],
    config: {
      callType: 'both',
      purpose: 'appointments',
      tone: 'professional',
      features: ['appointments', 'reminders', 'information', 'transfer'],
      systemPrompt: 'You are a professional medical office assistant helping with appointment scheduling, sending reminders, and collecting patient information. Maintain patient privacy and professionalism.'
    }
  },
  {
    id: 'sales',
    name: 'Sales Team',
    icon: TrendingUp,
    description: 'Optimized for lead qualification, follow-ups, and booking sales calls',
    features: [
      'Qualify leads',
      'Schedule demos',
      'Follow up automatically',
      'CRM integration ready'
    ],
    config: {
      callType: 'outgoing',
      purpose: 'sales',
      tone: 'professional',
      features: ['appointments', 'information', 'follow-ups'],
      systemPrompt: 'You are an enthusiastic sales assistant helping qualify leads, schedule product demos, and follow up with potential customers. Focus on understanding their needs and booking qualified meetings.'
    }
  },
  {
    id: 'payment',
    name: 'Payment Collection',
    icon: CreditCard,
    description: 'Specialized for collecting payments, processing orders, and handling billing inquiries',
    features: [
      'Collect payment details',
      'Process orders securely',
      'Answer billing questions',
      'Send payment confirmations'
    ],
    config: {
      callType: 'both',
      purpose: 'payment',
      tone: 'professional',
      features: ['payment', 'information', 'transfer'],
      systemPrompt: 'You are a professional payment specialist helping customers with payment collection, order processing, and billing inquiries. Ensure secure handling of payment information and provide clear confirmation of transactions.'
    }
  },
  {
    id: 'lead-qualification',
    name: 'Lead Qualification',
    icon: UserCheck,
    description: 'Designed to qualify and score leads before routing to sales team',
    features: [
      'Ask qualifying questions',
      'Score lead quality',
      'Gather contact info',
      'Route to right team'
    ],
    config: {
      callType: 'both',
      purpose: 'lead-qualification',
      tone: 'friendly',
      features: ['information', 'appointments', 'transfer'],
      systemPrompt: 'You are a lead qualification specialist helping identify potential customers, gather key information, and score leads based on their needs and fit. Ask targeted questions to understand budget, timeline, and decision-making authority.'
    }
  },
  {
    id: 'appointments',
    name: 'Appointment Scheduling',
    icon: Calendar,
    description: 'Focused on scheduling, rescheduling, and managing appointments efficiently',
    features: [
      'Schedule new appointments',
      'Handle rescheduling',
      'Send reminders',
      'Check availability'
    ],
    config: {
      callType: 'both',
      purpose: 'appointments',
      tone: 'friendly',
      features: ['appointments', 'reminders', 'information'],
      systemPrompt: 'You are an appointment scheduling specialist helping customers book, reschedule, and manage appointments. Check availability, send confirmations, and provide reminders to ensure smooth scheduling.'
    }
  }
];

const TemplateSelector = ({ onSelect, onBack, onCustom }: TemplateSelectorProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-3xl font-bold text-foreground">{t('onboarding.templateSelector.title')}</h2>
        <Button
          variant="outline"
          onClick={onCustom}
          className="border-primary/50 hover:bg-primary/10"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {t('onboarding.templateSelector.buildByChat')}
        </Button>
      </div>

      <p className="text-muted-foreground text-lg mb-8 text-center">
        {t('onboarding.templateSelector.subtitle')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card
            key={template.id}
            className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group p-6"
            onClick={() => onSelect(template.config)}
          >
            <div className="flex flex-col h-full">
              <div className="mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <template.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{t(`onboarding.templateSelector.templates.${template.id}.name`)}</h3>
                <p className="text-muted-foreground text-sm mb-4">{t(`onboarding.templateSelector.templates.${template.id}.description`)}</p>
              </div>

              <div className="flex-1">
                <h4 className="text-sm font-semibold text-foreground mb-2">{t('onboarding.templateSelector.includes')}</h4>
                <ul className="space-y-2">
                  {template.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-primary mt-0.5">✓</span>
                      <span>{t(`onboarding.templateSelector.templates.${template.id}.features.${idx}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                className="w-full mt-6 bg-primary hover:bg-primary/90"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(template.config);
                }}
              >
                {t('onboarding.templateSelector.useTemplate')}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-8 p-6 bg-muted/50 rounded-xl border border-border">
        <p className="text-sm text-muted-foreground text-center">
          💡 <strong>{t('onboarding.templateSelector.tip.label')}</strong> {t('onboarding.templateSelector.tip.text')}
        </p>
      </div>
    </div>
  );
};

export default TemplateSelector;
